import React, { RefObject } from "react";
import { LighthouseData } from "./utils/lighthouseConstants";
import VersionManagementService from "@arcgis/core/versionManagement/VersionManagementService";
import EditorViewModel from "@arcgis/core/widgets/Editor/EditorViewModel";
import type Layer from "@arcgis/core/layers/Layer";
import type Point from "@arcgis/core/geometry/Point";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Graphic from "@arcgis/core/Graphic";
import type { SearchResults } from "@arcgis/core/widgets/Search/types";
import type PortalUser from "@arcgis/core/portal/PortalUser";

export interface UserContextType {
    user: {
        set: (value: PortalUser | null) => void;
        value: PortalUser | null;
    };
    userSelf: {
        set: (value: any) => void;
        value: any;
    };
}

export interface VersionContextType {
    versionServiceRef: {
        set: (value: React.RefObject<VersionManagementService>) => void;
        value: React.RefObject<VersionManagementService>;
    };
}

/**
 * A lighthouse placed on the map but not yet created, held while the user decides whether to
 * use the default sectors or define them in a table.
 */
export interface PendingLighthousePlacement {
    feature: Graphic;
    center: Point;
}

export interface AppContextType {
    selectedFeature: {
        set: (value: Graphic | null) => void;
        value: Graphic | null;
    };
    shownFeature: {
        set: (value: Graphic | null) => void;
        value: Graphic | null;
    };
    searchResults: {
        set: (value: SearchResults[]) => void;
        value: SearchResults[];
    };
    clickResults: {
        set: (value: Graphic[]) => void;
        value: Graphic[];
    };
    efsSelectedFeatures: {
        add: (value: Graphic) => void;
        remove: (value: Graphic) => void;
        value: Graphic[];
        clear: () => void;
    };
    morildExportSelectedFeatures: {
        add: (value: Graphic) => void;
        remove: (value: Graphic) => void;
        addMany: (values: Graphic[]) => void;
        clear: () => void;
        value: Graphic[];
    };
    seacableStartFeature: {
        set: (value: Graphic | null) => void;
        value: Graphic | null;
    };
    seacableEndFeature: {
        set: (value: Graphic | null) => void;
        value: Graphic | null;
    };
    createObjectCurrentEntryRef: {
        set: (value: React.RefObject<string | null>) => void;
        value: React.RefObject<string | null>;
    };
    pendingLighthousePlacement: {
        set: (value: React.RefObject<PendingLighthousePlacement | null>) => void;
        value: React.RefObject<PendingLighthousePlacement | null>;
    };
    selectedFeatureHighlightRef: {
        set: (value: React.RefObject<ResourceHandle | null>) => void;
        value: React.RefObject<ResourceHandle | null>;
    };
    /**
     * Counter of in-flight attribute saves against the selected feature. Used by callers
     * that need to guarantee they're operating on settled data — most importantly the
     * direct-to-default fast-path post, which would otherwise race against a save whose
     * network call hasn't returned yet. Callers wrap saves in `begin()` / `end()` so the
     * counter is balanced even when the save fails.
     */
    featureEditsInFlight: {
        value: number;
        begin: () => void;
        end: () => void;
    };
}

export interface MenuContextType {}

export interface LighthouseContextType {
    lighthouseData: {
        set: (value: LighthouseData) => void;
        value: LighthouseData;
    };

    lighthouseDataRef: {
        set: (value: React.RefObject<LighthouseData>) => void;
        value: React.RefObject<LighthouseData>;
    };

    lighthouseSectorPanelsOpen: {
        set: (value: Set<number>) => void;
        value: Set<number>;
    };

    selectedLineRef: {
        set: (value: RefObject<Graphic | null>) => void;
        value: RefObject<Graphic | null>;
    };
}

export interface JobContextType {
    selectedJob: {
        set: (value: Graphic | null) => void;
        value: Graphic | null;
    };
    preLoadJobConfig: {
        set: (value: { layerID: number; objectId: string } | null) => void;
        value: { layerID: number; objectId: string } | null;
    };
}

export interface OverettContextType {
    overettObjects: {
        set: (value: { upper: Graphic | null | undefined; lower: Graphic | null | undefined }) => void;
        value: { upper: Graphic | null | undefined; lower: Graphic | null | undefined };
    };

    overettLinjeRef: {
        set: (value: RefObject<Graphic | null>) => void;
        value: RefObject<Graphic | null>;
    };

    overettHighlightsRef: {
        set: (value: RefObject<ResourceHandle[]>) => void;
        value: RefObject<ResourceHandle[]>;
        store: (handles: ResourceHandle[] | null, clear?: boolean) => void;
        clear: () => void;
    };

    overettObserver: {
        set: (value: Point | null) => void;
        value: Point | null;
    };

    editorVMUpperOverett: {
        set: (value: React.RefObject<EditorViewModel | null | undefined>) => void;
        value: React.RefObject<EditorViewModel | null | undefined>;
    };

    editorVMLowerOverett: {
        set: (value: React.RefObject<EditorViewModel | null | undefined>) => void;
        value: React.RefObject<EditorViewModel | null | undefined>;
    };
}

export interface FeatureTableContextType {
    activeLayer: {
        set: (value: Layer | undefined) => void;
        value: Layer | undefined;
    };
    isOpen: {
        set: (value: boolean) => void;
        value: boolean;
    };
}

export interface MenuContextType {}

export const UserContext = React.createContext<UserContextType | undefined>(undefined);
export const VersionContext = React.createContext<VersionContextType | undefined>(undefined);
export const MenuContext = React.createContext<MenuContextType | undefined>(undefined);
export const AppContext = React.createContext<AppContextType | undefined>(undefined);
export const LighthouseContext = React.createContext<LighthouseContextType | undefined>(undefined);
export const JobContext = React.createContext<JobContextType | undefined>(undefined);
export const OverettContext = React.createContext<OverettContextType | undefined>(undefined);
export const FeatureTableContext = React.createContext<FeatureTableContextType | undefined>(undefined);
